import React, { memo, useRef, useState } from "react"
import { Box, Dialog, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from "@material-ui/core"
import { Alarm } from "@material-ui/icons"
import { CreatorActions, DialogTitleCloser, TextDivider, DateTimeInputs, TileButton, Blockquote } from ".."
import { AssignerDisplay } from "./AssignerDisplay"
import { PROCESS_REPEAT, JOB_PINNING } from "../../data"
import { disableBackdropClick, useDateUtils, useFormUtils, useMountEffect, useProcessUtils } from "../../utils"
import { OutlinedSelect } from "../OutlinedSelect"
import { ColumnBox, RowBox } from "../Boxes"
import Checkbox from "../Checkbox"
import { StaticButton } from "../Buttons"
import { ProcessScheduleStatement } from "./ProcessScheduleStatement"

const typename = "__typename"

const none = {
  weekdays: {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  },
  months: {
    january: false,
    february: false,
    march: false,
    april: false,
    may: false,
    june: false,
    july: false,
    august: false,
    september: false,
    october: false,
    november: false,
    december: false,
  },
}

const ProcessScheduleDialog = ({ value, open, onClose, onChange }) => {
  const { isValid } = useFormUtils()
  const { scheduleDateToMoment, momentToScheduleDate } = useDateUtils()
  const { canScheduleOnlyPinPeriodTypes, canScheduleHaveNextInstancePinningType, isPeriodProcessPinningType } =
    useProcessUtils()
  const pinnedMinutesRef = useRef(null)

  const [after, setAfter] = useState(null)
  const [due, setDue] = useState(null)
  const [pinningType, setPinningType] = useState(JOB_PINNING.NONE)
  const [pinningPeriod, setPinningPeriod] = useState("")
  const [type, setType] = useState(PROCESS_REPEAT.DAILY)
  const [frequency, setFrequency] = useState(1)
  const [weekdays, setWeekdays] = useState({ ...none.weekdays })
  const [months, setMonths] = useState(["all"])

  const canOnlyPinPeriodTypes = canScheduleOnlyPinPeriodTypes(type)
  const hasPinningType = pinningType !== JOB_PINNING.NONE
  const hasPinningTypeNextInstance = canScheduleHaveNextInstancePinningType(type)
  const hasPinningPeriod = isPeriodProcessPinningType(pinningType)

  useMountEffect(() => {
    setAfter(scheduleDateToMoment(value.after))
    setDue(scheduleDateToMoment(value.due))
    setPinningType(value.pinning.type)
    setPinningPeriod(value.pinning.period)
    if (value.repeat) {
      let selectedMonths = Object.keys(value.repeat.months).filter(
        (month) => month !== typename && value.repeat.months[month]
      )
      if (!selectedMonths.length) selectedMonths = ["all"]

      setType(value.repeat.type)
      setFrequency(value.repeat.frequency)
      setWeekdays((prev) => {
        Object.keys(prev).forEach((weekday) => (prev[weekday] = value.repeat.weekdays[weekday]))
        return { ...prev }
      })
      setMonths(selectedMonths)
    }
  })

  const clearDates = () => {
    setAfter(null)
    setDue(null)
  }

  const clearWeekdays = () => {
    setWeekdays({ ...none.weekdays })
  }
  const clearMonths = () => {
    setMonths(["all"])
  }
  const clearRestrictedPinningTypes = (newType) => {
    if (
      (!canScheduleHaveNextInstancePinningType(newType) && pinningType === JOB_PINNING.NEXT_INSTANCE) ||
      (canScheduleOnlyPinPeriodTypes(newType) && !isPeriodProcessPinningType(pinningType))
    ) {
      setPinningType(JOB_PINNING.NONE)
    }
  }

  const compileData = () => {
    const data = {
      after: momentToScheduleDate(after),
      due: momentToScheduleDate(due),
      pinning: {
        type: pinningType,
        period: pinningPeriod ? Number(pinningPeriod) : null,
      },
      repeat: {
        type,
        frequency,
        weekdays,
        months: { ...none.months },
      },
    }

    months.filter((month) => month !== "all").forEach((month) => (data.repeat.months[month] = true))

    return data
  }

  const handleChange = () => {
    onChange && onChange(compileData())
  }

  const handleAfterChanged = (date) => setAfter(date)

  const handleDueChanged = (date) => setDue(date)

  const handleTypeChange = (newType) => {
    setType(newType)
    if (newType === PROCESS_REPEAT.ADHOC) clearDates()
    setFrequency(1)
    setPinningPeriod("")
    clearWeekdays()
    if ([PROCESS_REPEAT.NEVER, PROCESS_REPEAT.YEARLY, PROCESS_REPEAT.ADHOC].includes(newType)) clearMonths()
    clearRestrictedPinningTypes(newType)
  }

  const handleFrequencyChange = (event) => {
    setFrequency(event.target.value)
  }

  const handleMonthsChange = ({ target: { value: targetValues } }) => {
    if (months.includes("all") && targetValues.length > 1) {
      setMonths(targetValues.filter((targetValue) => targetValue !== "all"))
    } else if ((!months.includes("all") && targetValues.includes("all")) || !targetValues.length) {
      setMonths(["all"])
    } else {
      setMonths(targetValues)
    }
  }

  const handleWeekdayClick = (weekday) => {
    if (type === PROCESS_REPEAT.WEEKLY) {
      const newWeekdays = { ...weekdays, [weekday]: !weekdays[weekday] }
      if (!Object.keys(newWeekdays).some((key) => !newWeekdays[key])) {
        setType(PROCESS_REPEAT.DAILY)
        setWeekdays({ ...none.weekdays })
        clearRestrictedPinningTypes(PROCESS_REPEAT.DAILY)
        return
      }
    }

    setWeekdays((prev) => {
      prev[weekday] = !prev[weekday]
      return { ...prev }
    })
  }

  const handlePinningTypeChange = (event) => {
    if (!event) {
      const newDefaultPinningType = canOnlyPinPeriodTypes ? JOB_PINNING.HOURS : JOB_PINNING.COMPLETED
      const newHasPinningType = !hasPinningType
      setPinningType(hasPinningType ? JOB_PINNING.NONE : newDefaultPinningType)
      if (newHasPinningType && isPeriodProcessPinningType(newDefaultPinningType)) {
        setTimeout(() => pinnedMinutesRef.current.focus())
      }
      if (!newHasPinningType && pinningPeriod) {
        setPinningPeriod("")
      }
      return
    }
    const newValue = event.target.value
    setPinningType(newValue)

    if (isPeriodProcessPinningType(newValue)) {
      setTimeout(() => pinnedMinutesRef.current.focus())
    }
  }

  const handlePinningPeriodChange = ({ target: { value: newValue } }) => {
    setPinningPeriod(newValue)
  }

  const formValid = () => {
    if (hasPinningType && isPeriodProcessPinningType(type) && !pinningPeriod) {
      return false
    }
    if (type === PROCESS_REPEAT.NEVER) {
      return isValid(after)
    }
    if (type === PROCESS_REPEAT.ADHOC) {
      return true
    }
    return isValid(type, after)
  }

  const isFormValid = formValid()

  const pinningPeriodNumber = Number(pinningPeriod)

  return (
    <Dialog
      open={open}
      onClose={disableBackdropClick(onClose)}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="md"
      top={16}
      style={{ position: "fixed", top: 16 }}
    >
      <DialogTitle id="form-dialog-title">
        Set schedule
        <DialogTitleCloser onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TileButton
                title="Recurring"
                description="To be completed on a regular basis (e.g. daily, weekly)"
                active={![PROCESS_REPEAT.NEVER, PROCESS_REPEAT.ADHOC].includes(type)}
                onClick={() => handleTypeChange(PROCESS_REPEAT.DAILY)}
                style={{ width: "100%" }}
                data-cy="TileButton-recurring"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TileButton
                title="Ad hoc"
                description="To be completed only as needed by staff"
                active={type === PROCESS_REPEAT.ADHOC}
                onClick={() => handleTypeChange(PROCESS_REPEAT.ADHOC)}
                style={{ width: "100%" }}
                data-cy="TileButton-adhoc"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TileButton
                title="One off"
                description="To be completed only once on the chosen date"
                active={type === PROCESS_REPEAT.NEVER}
                onClick={() => handleTypeChange(PROCESS_REPEAT.NEVER)}
                style={{ width: "100%" }}
                data-cy="TileButton-oneoff"
              />
            </Grid>
          </Grid>
        </Box>

        {type !== PROCESS_REPEAT.ADHOC && (
          <>
            <TextDivider />

            <Box my={2}>
              <DateTimeInputs
                value={after}
                dueValue={due}
                onChange={handleAfterChanged}
                onDueChange={handleDueChanged}
                dateLabel="Start date"
                timeLabel="Available from"
                dueLabel="Due at (optional)"
                emptyTimeLabel="All day"
                emptyDueLabel="None"
                hasDue
              />
            </Box>
          </>
        )}

        {![PROCESS_REPEAT.NEVER, PROCESS_REPEAT.ADHOC].includes(type) && (
          <>
            <TextDivider />

            <Box my={2}>
              <Grid container spacing={1} direction="row" alignItems="center">
                <Grid item xs={12} sm={6}>
                  Repeat this process every:
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RowBox>
                    <Box mr={1}>
                      {[
                        PROCESS_REPEAT.DAILY,
                        PROCESS_REPEAT.WEEKLY,
                        // PROCESS_REPEAT.MONTHLY,
                        PROCESS_REPEAT.YEARLY,
                      ].includes(type) && (
                        <OutlinedSelect value={frequency} native={false} onChange={handleFrequencyChange}>
                          {type === PROCESS_REPEAT.DAILY &&
                            [1, 2, 3, 4, 5, 6, 7].map((item) => (
                              <MenuItem key={item} value={item}>
                                {item}
                              </MenuItem>
                            ))}
                          {type === PROCESS_REPEAT.WEEKLY &&
                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
                              <MenuItem key={item} value={item}>
                                {item}
                              </MenuItem>
                            ))}
                          {type === PROCESS_REPEAT.MONTHLY &&
                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map(
                              (item) => (
                                <MenuItem key={item} value={item}>
                                  {item}
                                </MenuItem>
                              )
                            )}
                          {type === PROCESS_REPEAT.YEARLY &&
                            [1, 2, 3].map((item) => (
                              <MenuItem key={item} value={item}>
                                {item}
                              </MenuItem>
                            ))}
                        </OutlinedSelect>
                      )}
                    </Box>
                    <OutlinedSelect
                      value={type}
                      native={false}
                      onChange={(event) => handleTypeChange(event.target.value)}
                    >
                      <MenuItem value={PROCESS_REPEAT.DAILY}>Day(s)</MenuItem>
                      <MenuItem value={PROCESS_REPEAT.WEEKLY}>Week(s)</MenuItem>
                      <MenuItem value={PROCESS_REPEAT.FORTNIGHTLY}>Fortnight</MenuItem>
                      <MenuItem value={PROCESS_REPEAT.MONTHLY}>Month</MenuItem>
                      <MenuItem value={PROCESS_REPEAT.YEARLY}>Year(s)</MenuItem>
                    </OutlinedSelect>
                  </RowBox>
                </Grid>
              </Grid>
            </Box>

            {type === PROCESS_REPEAT.WEEKLY && frequency === 1 && (
              <Box mb={3}>
                <RowBox mb={1}>On these days:</RowBox>
                <Grid container spacing={2}>
                  {Object.keys(none.weekdays).map((weekday) => (
                    <Grid key={weekday} item xs>
                      <StaticButton
                        text={`${weekday.substring(0, 1).toUpperCase()}${weekday.substring(1, 3)}`}
                        active={weekdays[weekday] === true}
                        onClick={() => handleWeekdayClick(weekday)}
                        style={{ width: "100%" }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {type !== PROCESS_REPEAT.YEARLY && (
              <Box>
                <OutlinedSelect
                  label="Repeat in these months"
                  placeholder="All months"
                  value={months}
                  native={false}
                  multiple
                  onChange={handleMonthsChange}
                >
                  <MenuItem value="all">All</MenuItem>
                  {Object.keys(none.months).map((month) => (
                    <MenuItem key={month} value={month}>{`${month.substring(0, 1).toUpperCase()}${month.substring(
                      1
                    )}`}</MenuItem>
                  ))}
                </OutlinedSelect>
              </Box>
            )}
          </>
        )}

        <Box mt={2}>
          <TextDivider />

          <ColumnBox mt={1} mb={2}>
            <RowBox mb={hasPinningType ? 1 : 0}>
              <Checkbox
                type="label"
                label={
                  <RowBox>
                    <Box mr={1}>Pin this job</Box>
                    {hasPinningType && (
                      <Box>
                        <OutlinedSelect
                          value={pinningType}
                          native={false}
                          size="small"
                          onChange={handlePinningTypeChange}
                        >
                          {!canOnlyPinPeriodTypes && <MenuItem value={JOB_PINNING.COMPLETED}>until completed</MenuItem>}
                          <MenuItem value={JOB_PINNING.HOURS}>for several hours (specify)</MenuItem>
                          <MenuItem value={JOB_PINNING.DAYS}>for several days (specify)</MenuItem>
                          {hasPinningTypeNextInstance && (
                            <MenuItem value={JOB_PINNING.NEXT_INSTANCE}>until its next scheduled occurrence</MenuItem>
                          )}
                        </OutlinedSelect>
                      </Box>
                    )}
                    {hasPinningPeriod && (
                      <RowBox ml={1}>
                        <TextField
                          type="number"
                          variant="outlined"
                          value={pinningPeriod}
                          onChange={handlePinningPeriodChange}
                          required
                          inputRef={pinnedMinutesRef}
                          inputProps={{ "data-cy": "TextField-pinned-for", min: 0 }}
                          style={{ width: 80 }}
                          size="small"
                        />
                        <Box ml={1}>
                          <>
                            {pinningType === JOB_PINNING.MINUTES && "minutes"}
                            {pinningType === JOB_PINNING.HOURS && "hours"}
                            {pinningType === JOB_PINNING.DAYS && "days"}
                          </>{" "}
                          after available
                        </Box>
                      </RowBox>
                    )}
                  </RowBox>
                }
                checked={hasPinningType}
                onChange={() => handlePinningTypeChange()}
              />
            </RowBox>
            {!hasPinningType && (
              <Typography variant="caption">
                This job will remain at the top of each day until it meets the conditions you specify.
              </Typography>
            )}
            {pinningType === JOB_PINNING.COMPLETED && (
              <Typography variant="caption">This job will remain at the top of each day until completed.</Typography>
            )}
            {pinningType === JOB_PINNING.MINUTES && (
              <Typography variant="caption">
                This job will remain at the top for {pinningPeriod || "several"} minutes after available from.
              </Typography>
            )}
            {pinningType === JOB_PINNING.HOURS && (
              <Typography variant="caption">
                This job will remain at the top for {pinningPeriod || "several"} hours after available from.
              </Typography>
            )}
            {pinningType === JOB_PINNING.DAYS && (
              <Typography variant="caption">
                {!pinningPeriod && (
                  <>This job will remain at the top for the specificed number of days after available from.</>
                )}
                {pinningPeriodNumber === 1 && (
                  <>This job will remain at the top an additional day after available from.</>
                )}
                {pinningPeriodNumber > 1 && (
                  <>This job will remain at the top an additional {pinningPeriod} days after available from.</>
                )}
              </Typography>
            )}
            {pinningType === JOB_PINNING.NEXT_INSTANCE && (
              <Typography variant="caption">
                This job will remain at the top of each day until completed otherwise its next scheduled occurrence. Use
                this to avoid multiple active pinned instances of this job.
              </Typography>
            )}
            {/* <Typography variant="caption">
                If chosen, this job will remain at the top until the time entered passes. Use this if you have jobs that
                run past midnight to keep them pinned.
              </Typography> */}
          </ColumnBox>
        </Box>

        <Box mt={2} mb={1}>
          <TextDivider />
        </Box>

        {isFormValid && ![PROCESS_REPEAT.NEVER, PROCESS_REPEAT.ADHOC].includes(type) && (
          <Box mt={2} mb={1}>
            <Blockquote body={<ProcessScheduleStatement process={compileData()} />} />
          </Box>
        )}

        {type === PROCESS_REPEAT.ADHOC && (
          <Box mt={2} mb={1}>
            <Blockquote
              title="About ad hoc processes"
              body="This process has no fixed date or time, and will only run when required by staff. Only the staff you have assigned to this process will be able to run it."
            />
          </Box>
        )}

        <CreatorActions
          id="ProcessScheduleAssigner-CreatorActions"
          subject="Process"
          cancelLabel="Back"
          submitLabel="Set schedule"
          onClose={onClose}
          onSubmit={handleChange}
          disableSubmit={!isFormValid}
        />
      </DialogContent>
    </Dialog>
  )
}

const ProcessScheduleAssigner = ({
  title = "Schedule",
  placeholder = "Not scheduled",
  process,
  topBorder = true,
  onChange,
  disabled,
  cy,
}) => {
  const [open, setOpen] = useState(false)

  const handleDisplayClick = () => {
    setOpen(true)
  }

  const handleChange = (newValue) => {
    onChange && onChange(newValue)
    setOpen(false)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      {open && <ProcessScheduleDialog open={open} value={process} onChange={handleChange} onClose={handleClose} />}

      <AssignerDisplay
        icon={<Alarm />}
        title={title}
        value={process?.repeat ? <ProcessScheduleStatement process={process} /> : null}
        placeholder={placeholder}
        onClick={handleDisplayClick}
        topBorder={topBorder}
        disabled={disabled}
        cy={cy}
      />
    </>
  )
}

const ProcessScheduleAssignerMemo = memo(ProcessScheduleAssigner)

export { ProcessScheduleAssignerMemo as ProcessScheduleAssigner }
